<template>
  <article
    class="rec-card pl-card"
    :class="[`rec-${tone}`, { 'rec-top-row': emphasis, 'rec-compact': compact }]"
    :aria-busy="busy ? 'true' : 'false'"
  >
    <div class="pl-top rec-head">
      <h3 class="rec-title">{{ row.title }}</h3>
      <span class="pl-chip" :class="mayaRow ? 'pl-m' : 'pl-c'">{{ mayaRow ? "Maya" : "You" }}</span>
      <span class="rec-state" :class="`rec-state-${tone}`">{{ label }}</span>
    </div>

    <p v-if="lift" class="pl-why rec-lift">{{ lift }}</p>
    <ul v-if="!compact && evidence.length" class="rec-ev">
      <li v-for="(line, index) in evidence" :key="index">{{ line }}</li>
    </ul>
    <p v-if="verify" class="rec-verify">{{ verify }}</p>
    <p v-if="row.dismissed_reason" class="rec-verify">Skipped: {{ row.dismissed_reason }}</p>

    <p v-if="upgrade" class="rec-upgrade" role="status">
      {{ upgrade.message }}
      <router-link class="rec-upgrade-link" to="/account/billing">See plans</router-link>
    </p>
    <p v-if="error" class="rec-error" role="alert">{{ error }}</p>

    <div v-if="actions.length" class="pl-acts rec-acts">
      <button
        v-for="action in actions"
        :key="action.id"
        type="button"
        class="pl-go"
        :class="{ ghost: !action.primary }"
        :disabled="Boolean(busy)"
        @click="run(action.id)"
      >{{ busy === action.id ? "Working…" : action.label }}</button>
    </div>

    <div v-if="skipping" class="rec-reasons" role="group" aria-label="Why are you skipping this?">
      <span class="rec-reasons-lab">Why skip it?</span>
      <button v-for="reason in DISMISS_REASONS" :key="reason" type="button" class="atbtn rec-reason" :disabled="Boolean(busy)" @click="dismiss(reason)">{{ reason }}</button>
      <button type="button" class="atbtn rec-reason" :disabled="Boolean(busy)" @click="skipping = false">Cancel</button>
    </div>
  </article>
</template>

<script setup>
import { computed, ref } from "vue";

import { dismissRecommendation, doneRecommendation, startRecommendation } from "../api/geoExec";
import {
  DISMISS_REASONS,
  actionsFor,
  evidenceLines,
  expectedLift,
  isMayaRow,
  statusLabel,
  statusTone,
  upgradeNotice,
  verifySummary,
} from "../composables/recommendations";
import { noteFeature } from "../stores/geoExecFeatures";

const props = defineProps({
  row: { type: Object, required: true },
  emphasis: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(["changed", "gone"]);

const busy = ref("");
const error = ref("");
const upgrade = ref(null);
const skipping = ref(false);

const tone = computed(() => statusTone(props.row));
const label = computed(() => statusLabel(props.row));
const mayaRow = computed(() => isMayaRow(props.row));
const lift = computed(() => expectedLift(props.row));
const evidence = computed(() => evidenceLines(props.row));
const verify = computed(() => verifySummary(props.row));
const actions = computed(() => actionsFor(props.row));

function run(id) {
  if (id === "skip") {
    skipping.value = !skipping.value;
    return;
  }
  if (id === "start") return call("start", () => startRecommendation(props.row.id));
  if (id === "done") return call("done", () => doneRecommendation(props.row.id));
  return undefined;
}

function dismiss(reason) {
  skipping.value = false;
  return call("skip", () => dismissRecommendation(props.row.id, reason));
}

async function call(id, request) {
  busy.value = id;
  error.value = "";
  upgrade.value = null;
  try {
    const result = await request();
    // The status the server reports, never one we assumed: a call that answered without moving the
    // row must leave the card exactly where it was.
    if (result?.status) emit("changed", { id: props.row.id, status: result.status, result });
    else emit("changed", { id: props.row.id, status: props.row.status, result });
  } catch (caught) {
    const notice = upgradeNotice(caught);
    if (notice) {
      // 402: the row does NOT move. Say why, and put the upgrade beside the button rather than
      // in place of it — the button is the right button again the moment the plan allows it.
      upgrade.value = notice;
    } else if (noteFeature("recommendations", caught)) {
      emit("gone");
    } else {
      error.value = caught.message || "That did not go through. Try again.";
    }
  } finally {
    busy.value = "";
  }
}
</script>

<style scoped>
.rec-card {
  margin-bottom: 10px;
}

.rec-head {
  gap: 8px;
}

.rec-title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--fs-subhead);
  font-weight: 700;
  color: var(--ink);
}

.rec-state {
  font-size: var(--fs-caption2);
  font-weight: 650;
  color: var(--mute);
  font-variant-numeric: tabular-nums;
}

.rec-state-working { color: var(--indigo); }
.rec-state-done { color: var(--jade); }
.rec-state-bad { color: var(--verm); }

.rec-lift {
  margin: 6px 0 0;
}

.rec-ev {
  margin: 8px 0 0;
  padding-left: 18px;
  color: var(--mute);
  font-size: var(--fs-caption);
}

.rec-ev li + li { margin-top: 2px; }

.rec-verify {
  margin: 8px 0 0;
  color: var(--mute);
  font-size: var(--fs-caption);
}

.rec-upgrade {
  margin: 8px 0 0;
  padding: 8px 10px;
  border: 1px solid var(--tint-line);
  border-radius: 10px;
  background: var(--tint-weak);
  color: var(--body);
  font-size: var(--fs-caption);
}

.rec-upgrade-link {
  margin-left: 6px;
  color: var(--indigo);
  font-weight: 650;
}

.rec-error {
  margin: 8px 0 0;
  color: var(--verm);
  font-size: var(--fs-caption);
  font-weight: 650;
}

.rec-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.rec-acts .pl-go {
  min-height: 36px;
  transition: transform 100ms ease-out;
}

.rec-acts .pl-go:active {
  transform: scale(0.97);
}

.rec-acts .pl-go:disabled {
  cursor: wait;
  opacity: 0.65;
}

.rec-reasons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.rec-reasons-lab {
  color: var(--mute);
  font-size: var(--fs-caption);
}

.rec-reason {
  font-size: var(--fs-caption);
  padding: 5px 11px;
}

/* One row at a time: the first thing to do is emphasised, the rest stay readable. */
.rec-top-row {
  border-color: var(--tint-line);
  box-shadow: var(--shadow-1);
}

.rec-top-row .rec-title::before {
  content: "Start here · ";
  color: var(--indigo);
  font-weight: 700;
}

.rec-muted {
  opacity: 0.62;
}

.rec-compact {
  margin-bottom: 0;
  padding: 12px 14px;
}

@media (prefers-reduced-motion: reduce) {
  .rec-acts .pl-go { transition: none; }
  .rec-acts .pl-go:active { transform: none; }
}
</style>
